import { sanitizeRedirectPath } from "./loginRedirect";
import { printingStationFetch } from "@orizom/core";

const BASE_URL = import.meta?.env?.VITE_API_BASE_URL || "";

function buildUrl(path, params) {
  const url = path?.startsWith("http")
    ? new URL(path)
    : new URL((path?.startsWith("/") ? "" : "/") + (path || ""), window.location.origin + BASE_URL);

  if (params && typeof params === "object") {
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) v.forEach((vv) => url.searchParams.append(k, vv));
      else url.searchParams.set(k, String(v));
    });
  }

  return url.toString();
}

async function parseResponse(res, raw) {
  if (raw) return res;
  if (res.status === 204) return null;
  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  try {
    return isJson ? await res.json() : await res.text();
  } catch (e) {
    return null;
  }
}

function redirectToLoginIfNeeded(payload, response) {
  if (!payload || typeof payload !== "object") return;
  if (payload.code !== "NOT_LOGGED_IN") return;
  if (response?.status !== 401) return;

  const currentPath = window.location?.pathname || "/";
  const currentSearch = window.location?.search || "";
  const currentHash = window.location?.hash || "";
  const isAdminRoute = currentPath.startsWith("/admin");
  const loginPath = isAdminRoute ? "/admin/login" : "/login";

  if (!isAdminRoute && currentPath === "/") return;

  if (currentPath !== loginPath) {
    const redirect = sanitizeRedirectPath(
      `${currentPath}${currentSearch}${currentHash}`,
      { defaultPath: isAdminRoute ? "/admin" : "/", loginPath }
    );
    const loginUrl = `${loginPath}?redirect=${encodeURIComponent(redirect)}`;
    // Use replace to avoid keeping broken session state in history.
    window.location.replace(loginUrl);
  }
}

function withTimeout(signal, ms) {
  if (!ms) return { signal, cancel: () => {} };
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(new DOMException("Timeout", "TimeoutError")), ms);

  const linked = new AbortController();
  const onAbort = () => linked.abort();
  if (signal) signal.addEventListener("abort", onAbort, { once: true });

  const forward = () => ctrl.signal.aborted && linked.abort();
  ctrl.signal.addEventListener("abort", forward);

  return {
    signal: linked.signal,
    cancel: () => {
      clearTimeout(timer);
      ctrl.signal.removeEventListener("abort", forward);
      if (signal) signal.removeEventListener("abort", onAbort);
    },
  };
}

async function request(method, path, options = {}) {
  const {
    headers = {},
    params,
    body,
    timeout, // ms
    raw = false, // return Response instead of parsed
    signal,
    credentials, // e.g. "include"
  } = options;

  const url = buildUrl(path, params);

  const finalHeaders = new Headers(headers);

  const requestCredentials = credentials ?? "include";

  let bodyToSend = body;
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const isBlob = typeof Blob !== "undefined" && body instanceof Blob;
  const isJsonBody = body && !isFormData && !isBlob && typeof body !== "string";

  if (isJsonBody) {
    if (!finalHeaders.has("Content-Type")) finalHeaders.set("Content-Type", "application/json");
    bodyToSend = JSON.stringify(body);
  }
  if (!finalHeaders.has("Accept")) finalHeaders.set("Accept", "application/json");

  const { signal: timeoutSignal, cancel } = withTimeout(signal, timeout || 0);

  let res;
  try {
    res = await printingStationFetch(url, {
      method,
      headers: finalHeaders,
      body: ["GET", "HEAD"].includes(method) ? undefined : bodyToSend,
      signal: timeoutSignal,
      credentials: requestCredentials,
    });
  } catch (err) {
    cancel();
    const isAbort = err?.name === "AbortError" || err?.name === "TimeoutError";
    throw {
      ok: false,
      status: 0,
      message: isAbort ? "Request aborted/timeout" : (err?.message || "Network error"),
      error: err,
    };
  }
  cancel();

  const data = await parseResponse(res, raw);
  if (!raw) {
    redirectToLoginIfNeeded(data, res);
  }
  if (!res.ok) {
    const message = (data && (data.message || data.error)) || `HTTP ${res.status}`;
    const err = { ok: false, status: res.status, data, message };
    // Optional: handle 401 globally (e.g., redirect). Left to caller/Auth.
    throw err;
  }

  return data;
}

const api = {
  get: (path, opts) => request("GET", path, opts),
  post: (path, body, opts = {}) => request("POST", path, { ...opts, body }),
  put: (path, body, opts = {}) => request("PUT", path, { ...opts, body }),
  patch: (path, body, opts = {}) => request("PATCH", path, { ...opts, body }),
  delete: (path, opts) => request("DELETE", path, opts),
  upload: (path, formData, opts = {}) => request("POST", path, { ...opts, body: formData }),
  raw: (method, path, opts = {}) => request(method, path, { ...opts, raw: true }),
};

export default api;
